import { useState } from 'react'
import {
  EmptyRow, ErpTable, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import {
  DEFAULT_COUNTRY, PhoneInput, countryOf, phoneIsValid,
} from '@/components/erp/phone-input'
import {
  archiveContact, contactDispositions, contactKinds, listContacts, restoreContact,
  saveContact, type Contact,
} from './api'

/**
 * The campaign address book.
 *
 * ## Who this is for
 *
 * The county officer, the chief, the bishop, the printer, the journalist. They are neither
 * on our voter register nor on our staff, so until now they lived in one person's phone —
 * and when that person left, the campaign lost them.
 *
 * ## Every contact shows who owns the relationship
 *
 * Not to assign blame. The point of the table is that the campaign knows who to ask, and a
 * contact with no owner is the one that goes missing first — so the column is always there
 * and an empty one reads as a gap, which it is.
 *
 * ## The number is masked and there is no way to reveal it
 *
 * `+2547••••1234` is enough to recognise a number you already know and not enough to dial
 * one you don't. A list of four hundred names must not double as a way to take four hundred
 * numbers off a screen.
 */
export function ContactsPage() {
  const canRead = useCan('contact:read')
  const canManage = useCan('contact:manage')

  const [q, setQ] = useState('')
  const [kind, setKind] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [editing, setEditing] = useState<Contact | 'new' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const rows = useLoad(
    () => listContacts({
      q: q || undefined,
      kind: kind || undefined,
      includeArchived: showArchived,
      size: 200,
    }),
    [q, kind, showArchived])
  const kinds = useLoad(contactKinds, [])

  if (!canRead) return <Forbidden what="The campaign address book" />

  const contacts = rows.data?.content ?? []

  async function onArchive(contact: Contact) {
    setError(null)
    try {
      if (contact.archivedAt) {
        await restoreContact(contact.id)
      } else {
        await archiveContact(contact.id, null)
      }
      await rows.reload()
    } catch (e) {
      setError(explain(e))
    }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Contacts"
        description="Everybody the campaign deals with who is not a voter on our register or
                     one of our own people — officials, clergy, journalists, suppliers, party
                     contacts. Each one shows who owns the relationship."
        right={(
          <div className="flex items-center gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name or organisation"
              className="h-9 w-48 rounded border border-border bg-background px-2 text-erp-sm"
            />
            <select value={kind} onChange={(e) => setKind(e.target.value)}
                    className="h-9 rounded border border-border bg-background px-2 text-erp-sm">
              <option value="">Everyone</option>
              {(kinds.data ?? []).map((k) => (
                <option key={k.code} value={k.code}>{k.label}</option>
              ))}
            </select>
            {canManage && (
              <button type="button" className="erp-btn-primary h-9 text-erp-sm"
                      onClick={() => setEditing('new')}>
                Add a contact
              </button>
            )}
          </div>
        )}
      />

      <ErrorStrip message={error ?? rows.error} />

      <label className="flex items-center gap-2 text-erp-xs text-muted-foreground">
        <input type="checkbox" checked={showArchived}
               onChange={(e) => setShowArchived(e.target.checked)} />
        Include archived
      </label>

      {rows.loading && contacts.length === 0 ? (
        <LoadingBlock label="Loading contacts…" />
      ) : (
        <Panel>
          <ErpTable head={(
            <>
              <Th>Name</Th>
              <Th>What they are</Th>
              <Th>Where</Th>
              <Th>Phone</Th>
              <Th>Who owns this relationship</Th>
              <Th>Last spoken to</Th>
              <Th />
            </>
          )}>
            {contacts.length === 0 ? (
              <EmptyRow colSpan={7}
                message="Nobody here yet. Add the people this campaign depends on who are not
                         on the voter register — the chief, the bishop, the printer, the
                         county officer." />
            ) : contacts.map((c) => (
              <tr key={c.id} className={c.archivedAt ? 'text-muted-foreground' : undefined}>
                <Td>
                  <span className="font-medium">{c.fullName}</span>
                  {c.title || c.organisation ? (
                    <div className="text-erp-xs text-muted-foreground">
                      {[c.title, c.organisation].filter(Boolean).join(' · ')}
                    </div>
                  ) : null}
                  {c.archivedAt ? (
                    <div className="text-erp-xs">archived</div>
                  ) : null}
                </Td>
                <Td>{c.kindLabel}</Td>
                <Td>{c.unitName ?? (
                  <span className="text-muted-foreground">not tied to a place</span>
                )}</Td>
                <Td className="tabular-nums">
                  {c.phoneMask ?? <span className="text-muted-foreground">—</span>}
                </Td>
                <Td>
                  {c.ownerName ?? (
                    /* An empty owner is a gap, and reads as one. */
                    <span className="text-destructive">nobody</span>
                  )}
                </Td>
                <Td className="tabular-nums">
                  {c.lastContactedOn ?? <span className="text-muted-foreground">—</span>}
                </Td>
                <Td>
                  {canManage ? (
                    <span className="flex gap-2">
                      <button type="button" onClick={() => setEditing(c)}
                              className="text-erp-xs underline underline-offset-2
                                         text-muted-foreground hover:text-foreground">
                        Edit
                      </button>
                      <button type="button" onClick={() => onArchive(c)}
                              className="text-erp-xs underline underline-offset-2
                                         text-muted-foreground hover:text-foreground">
                        {c.archivedAt ? 'Restore' : 'Archive'}
                      </button>
                    </span>
                  ) : null}
                </Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      )}

      {editing && (
        <ContactDialog
          contact={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); void rows.reload() }}
        />
      )}
    </div>
  )
}

function ContactDialog({ contact, onClose, onDone }: {
  contact: Contact | null
  onClose: () => void
  onDone: () => void
}) {
  const kinds = useLoad(contactKinds, [])
  const dispositions = useLoad(contactDispositions, [])
  const [busy, setBusy] = useState(false)
  /*
   * The dialog owns its own error. Putting it on the page behind made the one refusal that
   * actually happens — "that number is already filed under Joseph Kiprotich" — render under
   * the modal, so the form just appeared to do nothing and the reason had to be hunted for
   * by closing it.
   */
  const [failure, setFailure] = useState<string | null>(null)
  /*
   * The number and its country are separate state, held by the shared PhoneInput rather
   * than a free-text box. `0712345678` is a valid number in several countries and a
   * different person in each, so the country is not something the server can infer — and
   * the picker is what makes somebody choose rather than guess. Kenya by default.
   */
  const [phone, setPhone] = useState('')
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY)
  const [form, setForm] = useState({
    fullName: contact?.fullName ?? '',
    kindCode: contact?.kindCode ?? '',
    title: contact?.title ?? '',
    organisation: contact?.organisation ?? '',
    email: contact?.email ?? '',
    dispositionCode: contact?.dispositionCode ?? '',
    notes: contact?.notes ?? '',
    lastContactedOn: contact?.lastContactedOn ?? '',
  })

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement
    | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    // Caught here so a bad number never costs a round trip. The server re-validates with
    // its own libphonenumber — a browser check is a courtesy, never a control.
    if (phone && !phoneIsValid(phone)) {
      setFailure('That is not a number that can exist in the country selected. Check the '
        + 'country, or the digits.')
      return
    }
    setBusy(true)
    setFailure(null)
    try {
      await saveContact({
        fullName: form.fullName,
        kindCode: form.kindCode,
        title: form.title || null,
        organisation: form.organisation || null,
        // E.164 and the alpha-2 the number actually resolves to, not whatever the picker
        // happened to be left on.
        phoneCountry: phone ? countryOf(phone, phoneCountry) : null,
        phone: phone || null,
        email: form.email || null,
        dispositionCode: form.dispositionCode || null,
        notes: form.notes || null,
        lastContactedOn: form.lastContactedOn || null,
      }, contact?.id)
      onDone()
    } catch (err) {
      setFailure(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto
                    bg-black/40 p-6" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl rounded border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <h2 className="text-erp-base font-medium">
            {contact ? `Edit ${contact.fullName}` : 'Add a contact'}
          </h2>
          <button type="button" onClick={onClose}
                  className="text-erp-sm text-muted-foreground hover:text-foreground">
            Close
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3 p-4">
          {failure ? (
            <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2
                          text-erp-sm text-destructive">
              {failure}
            </p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <L label="Name">
              <input required value={form.fullName} onChange={set('fullName')}
                     className="erp-input" />
            </L>
            <L label="What they are to us">
              <select required value={form.kindCode} onChange={set('kindCode')}
                      className="erp-input">
                <option value="">Choose…</option>
                {(kinds.data ?? []).map((k) => (
                  <option key={k.code} value={k.code}>{k.label}</option>
                ))}
              </select>
            </L>
            <L label="Title or role">
              <input value={form.title} onChange={set('title')} className="erp-input"
                     placeholder="e.g. Chief, Kabarnet location" />
            </L>
            <L label="Organisation">
              <input value={form.organisation} onChange={set('organisation')}
                     className="erp-input" />
            </L>
            <L label="Phone"
               hint={contact?.hasPhone
                 ? `A number is already on file (${contact.phoneMask}). Enter one only to `
                   + 'replace it.'
                 : 'Stored encrypted. Only a masked version is ever shown back.'}>
              <PhoneInput
                value={phone}
                onChange={setPhone}
                country={phoneCountry}
                onCountryChange={setPhoneCountry}
              />
            </L>
            <L label="Email">
              <input type="email" value={form.email} onChange={set('email')}
                     className="erp-input" />
            </L>
            <L label="How they stand towards us"
               hint="Working knowledge. Anything that would embarrass the campaign if they
                     read it belongs nowhere.">
              <select value={form.dispositionCode} onChange={set('dispositionCode')}
                      className="erp-input">
                <option value="">Not recorded</option>
                {(dispositions.data ?? []).map((d) => (
                  <option key={d.code} value={d.code}>{d.label}</option>
                ))}
              </select>
            </L>
            <L label="Last spoken to">
              <input type="date" value={form.lastContactedOn}
                     onChange={set('lastContactedOn')} className="erp-input" />
            </L>
          </div>
          <L label="Notes">
            <textarea value={form.notes} onChange={set('notes')} rows={3}
                      className="erp-input" />
          </L>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
                    className="erp-btn-secondary h-9 text-erp-sm">Cancel</button>
            <button type="submit" disabled={busy}
                    className="erp-btn-primary h-9 text-erp-sm">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function L({ label, hint, children }: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="block text-erp-xs font-medium">{label}</span>
      {hint ? <span className="block text-erp-xs text-muted-foreground">{hint}</span> : null}
      <span className="mt-1 block">{children}</span>
    </label>
  )
}
